import { ArrowUpRight, Captions, Mic, Upload } from "lucide-react";

import { translations } from "utils/i18n";
import { useLanguage } from "context/languageContext";
import { cn } from "lib/utils";

// The three entry points for creating a transcription — the header row above
// the file list on the home page.
// The card itself is a plain flat surface; the icon badge is darker than the
// card (not a tinted color) so it still reads as a distinct badge.
// `disabled` is a transient state (an upload/recording is in flight) and
// fades every tile alike.
function ActionTile({ icon: Icon, title, description, disabled, ...props }) {
  return (
    <button
      type="button"
      data-slot="button"
      disabled={disabled}
      className={cn(
        "feature-tile group relative flex flex-col items-start gap-3 rounded-xl border border-border p-4 text-start disabled:cursor-not-allowed",
        disabled && "opacity-50"
      )}
      {...props}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <span className="feature-tile-icon flex size-9 shrink-0 items-center justify-center rounded-md text-foreground">
          <Icon className="size-4" aria-hidden="true" />
        </span>

        <ArrowUpRight
          className="size-4 -translate-y-1 translate-x-1 text-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:text-foreground group-hover:opacity-100 rtl:-scale-x-100"
          aria-hidden="true"
        />
      </div>

      <div className="grid gap-0.5">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <span className="text-xs leading-snug text-muted-foreground">{description}</span>
      </div>
    </button>
  );
}

export function ActionTiles({ onUpload, onRecord, onCaption, uploading, className }) {
  const { language } = useLanguage();
  const tr = translations[language].actionTiles;

  const actions = [
    { key: "upload", icon: Upload, title: tr.createTitle, description: tr.createDescription, onClick: onUpload },
    { key: "record", icon: Mic, title: tr.recordTitle, description: tr.recordDescription, onClick: onRecord },
    { key: "caption", icon: Captions, title: tr.captionTitle, description: tr.captionDescription, onClick: onCaption },
  ];

  return (
    <div className={cn("transcribe-actions grid grid-cols-1 gap-3 sm:grid-cols-3", className)}>
      {actions.map(({ key, ...action }) => (
        <ActionTile key={key} {...action} disabled={uploading} />
      ))}
    </div>
  );
}
